import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import City from "../three/City";
import DataStream from "../three/DataStream";
import PostFX from "../three/PostFX";
import CharacterController from "./CharacterController";
import ThirdPersonCameraRig from "./ThirdPersonCameraRig";
import FloorPath from "./FloorPath";
import POIMarkers from "./POIMarkers";
import ProjectPedestals from "./ProjectPedestals";
import Compass from "./Compass";
import MiniMap from "./MiniMap";
import SectionPanel from "./SectionPanel";
import ControlsHint from "./ControlsHint";
import SoundToggle from "../components/SoundToggle";
import ProjectModal from "../components/ProjectModal";
import WorldTitleSign from "./WorldTitleSign";
import { useAmbientSound } from "../lib/useAmbientSound";
import { generateBuildings } from "./buildingLayout";

export default function GameMode({ quality }) {
  const [activePOI, setActivePOI] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [hintDismissed, setHintDismissed] = useState(false);
  const visitedIds = useRef(new Set());
  const { enabled: soundEnabled, toggle: toggleSound } = useAmbientSound();

  const buildings = useMemo(
    () => generateBuildings(quality.buildingDetail === "low" ? 16 : 34),
    [quality.buildingDetail]
  );

  const handleActivePOIChange = useCallback((id) => {
    setActivePOI(id);
    if (id) visitedIds.current.add(id);
  }, []);

  const quality3d = useMemo(() => quality, [quality]);

  return (
    <div className="relative h-screen w-screen overflow-hidden" data-testid="game-mode-root">
      <Canvas
        dpr={quality3d.dpr}
        gl={{ antialias: false, powerPreference: "high-performance" }}
        camera={{ fov: 55, near: 0.1, far: 300, position: [0, 5, 28] }}
        data-testid="game-canvas"
      >
        <color attach="background" args={["#050505"]} />
        <fog attach="fog" args={["#050512", 15, 110]} />
        <ambientLight intensity={0.4} />
        <pointLight position={[0, 20, 20]} intensity={40} color="#01FFFF" distance={80} />
        <pointLight position={[10, 8, -30]} intensity={30} color="#FF007F" distance={70} />
        <pointLight position={[-8, 6, -60]} intensity={25} color="#01FFFF" distance={60} />

        <Suspense fallback={null}>
          <City buildings={buildings} />
          <DataStream count={Math.floor(quality3d.particleCount * 0.5)} />
          <FloorPath />
          <POIMarkers />
          <WorldTitleSign />
          <ProjectPedestals onSelect={setActiveProject} />
          <CharacterController
            buildings={buildings}
            onActivePOIChange={handleActivePOIChange}
            onMove={() => setHintDismissed(false)}
          />
        </Suspense>

        <ThirdPersonCameraRig />
        <PostFX bloomIntensity={quality3d.bloomIntensity} />
      </Canvas>

      <p
        data-testid="world-title-label"
        className="pointer-events-none fixed top-6 left-6 z-40 font-heading text-sm md:text-base uppercase tracking-wide text-[#FF007F]"
        style={{ textShadow: "0 0 14px rgba(255,0,127,0.6)" }}
      >
        Adlan MS's World
      </p>
      <SoundToggle enabled={soundEnabled} onToggle={toggleSound} />
      <Compass visitedIds={visitedIds} />
      <MiniMap visitedIds={visitedIds} />
      <SectionPanel activePOI={activePOI} />
      <ControlsHint dismissed={hintDismissed} onDismiss={() => setHintDismissed(true)} />
      <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
    </div>
  );
}
